import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode,
} from "react";
import { useARC } from "providers/ARC/ARCProvider";
import {
    getInsta360BaseUrl,
    getInsta360State,
    postInsta360RecordStart,
    postInsta360RecordStop,
} from "lib/r2c2360Api";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";

type Insta360SessionValue = {
    fsm: string;
    busy: boolean;
    baseUrl: string | null;
    ingestState: (state?: string) => void;
    startRecord: () => Promise<void>;
    stopRecord: () => Promise<void>;
};

const Insta360SessionContext = createContext<Insta360SessionValue | null>(null);
Insta360SessionContext.displayName = "Insta360Session";

export const useInsta360Session = (): Insta360SessionValue => {
    const value = useContext(Insta360SessionContext);
    if (!value) {
        throw new Error("useInsta360Session must be used within Insta360SessionProvider");
    }
    return value;
};

const Insta360SessionProvider = ({ children }: { children: ReactNode }) => {
    const { selectedARC } = useARC();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();
    const baseUrl = useMemo(
        () => getInsta360BaseUrl(selectedARC?.host ?? null, isSecure, arcId),
        [selectedARC?.host, isSecure, arcId]
    );
    const [fsm, setFsm] = useState("disconnected");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!baseUrl) {
            setFsm("disconnected");
            return;
        }
        let cancelled = false;
        const refresh = async () => {
            try {
                const data = await getInsta360State(baseUrl);
                if (!cancelled && data.state) setFsm(data.state);
            } catch {
                if (!cancelled) setFsm("disconnected");
            }
        };
        void refresh();
        const id = window.setInterval(() => void refresh(), 1000);
        return () => {
            cancelled = true;
            window.clearInterval(id);
        };
    }, [baseUrl]);

    const ingestState = useCallback((state?: string) => {
        if (state) setFsm(state);
    }, []);

    const startRecord = useCallback(async () => {
        if (!baseUrl) return;
        setBusy(true);
        try {
            const data = await postInsta360RecordStart(baseUrl);
            if (data.state) setFsm(data.state);
        } finally {
            setBusy(false);
        }
    }, [baseUrl]);

    const stopRecord = useCallback(async () => {
        if (!baseUrl) return;
        setBusy(true);
        try {
            const data = await postInsta360RecordStop(baseUrl);
            if (data.state) setFsm(data.state);
        } finally {
            setBusy(false);
        }
    }, [baseUrl]);

    const value = useMemo(
        () => ({ fsm, busy, baseUrl, ingestState, startRecord, stopRecord }),
        [fsm, busy, baseUrl, ingestState, startRecord, stopRecord]
    );

    return (
        <Insta360SessionContext.Provider value={value}>{children}</Insta360SessionContext.Provider>
    );
};

export default Insta360SessionProvider;
